/**
 * BackgroundSystem — procedural starfield background.
 *
 * Per user spec 2026-08-04: every visible element in the game is a
 * pure-white rectangle. The background is solid black + a sparse,
 * slowly drifting grid of *tiny white Graphics dots* — no PNG loading,
 * no sprite textures, no chance of a `__MISSING` texture showing up
 * as a green grid.
 *
 * Three layers, all rendered via `scene.add.graphics()` and rotated /
 * translated per-frame for parallax:
 *   1. Solid black backdrop (1280×720 to cover the right edge of the
 *      canvas during scroll).
 *   2. Static starfield — ~5 % of grid cells get a 1–2 px white dot.
 *   3. Moving 10×10 grid — every cell gets a 1–2 px dot that drifts left.
 *
 * Lifecycle:
 *   const bg = new BackgroundSystem(scene);
 *   await bg.build();           // draws the layers
 *   bg.update(deltaMs, speed);  // called from GameScene.update
 *   bg.destroy();               // cleanup on scene restart
 */
import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../Game";

interface ParallaxSprite {
  container: Phaser.GameObjects.Container;
  baseX: number;
  baseY: number;
  scrollFactor: number;
}

export class BackgroundSystem {
  private readonly scene: Phaser.Scene;
  private layers: Phaser.GameObjects.GameObject[] = [];
  private parallax: ParallaxSprite[] = [];
  // 10×10 grid of "background tiles" that drift left across the screen.
  private readonly gridCols = 10;
  private readonly gridRows = 10;
  // Fraction of cells that contain a star (5 %).
  private readonly starFraction = 0.05;
  // RNG for deterministic-looking but unique star placement.
  private rng: Phaser.Math.RandomDataGenerator;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.rng = new Phaser.Math.RandomDataGenerator(String(Date.now() & 0xffff));
  }

  /**
   * Build the background layers — no PNG loading, just procedural Graphics.
   * Safe to call once per scene create.
   */
  public async build(): Promise<void> {
    // Layer 0: solid black backdrop covering the canvas plus a tile to the
    // right so the moving-grid never reveals the void during scroll.
    const backdrop = this.scene.add.graphics();
    backdrop.fillStyle(0x000000, 1);
    backdrop.fillRect(-200, 0, GAME_WIDTH + 400, GAME_HEIGHT);
    this.layers.push(backdrop);

    // Layer 1: static starfield — 5% of the 10×10 grid cells get a tiny
    // white dot, scattered across the screen.
    const cellW = GAME_WIDTH / this.gridCols;
    const cellH = GAME_HEIGHT / this.gridRows;
    const starContainer = this.scene.add.container(0, 0);
    for (let r = 0; r < this.gridRows; r++) {
      for (let c = 0; c < this.gridCols; c++) {
        if (this.rng.frac() > this.starFraction) continue;
        const x = c * cellW + this.rng.realInRange(2, cellW - 4);
        const y = r * cellH + this.rng.realInRange(2, cellH - 4);
        const dot = this.scene.add.graphics();
        const radius = this.rng.realInRange(1, 2);
        const alpha = this.rng.realInRange(0.5, 1);
        dot.fillStyle(0xffffff, alpha);
        dot.fillCircle(x, y, radius);
        starContainer.add(dot);
      }
    }
    this.parallax.push({
      container: starContainer,
      baseX: 0,
      baseY: 0,
      scrollFactor: 0.05,
    });
    this.layers.push(starContainer);

    // Layer 2: a sparse, *randomly-positioned* parallax layer. Per
    // user feedback, the previous 10×10 grid layout was visibly regular
    // and read as "scaffolding" rather than stars. We now scatter ~30
    // dots across the canvas using a Poisson-style density check, so
    // no two dots are obviously the same distance from the camera.
    const foreground = this.scene.add.container(0, 0);
    const farStarCount = 30;
    for (let i = 0; i < farStarCount; i++) {
      const x = this.rng.realInRange(0, GAME_WIDTH);
      const y = this.rng.realInRange(0, GAME_HEIGHT);
      const radius = this.rng.realInRange(0.8, 2.2);
      const alpha = this.rng.realInRange(0.4, 0.95);
      const dot = this.scene.add.graphics();
      dot.fillStyle(0xffffff, alpha);
      dot.fillCircle(x, y, radius);
      foreground.add(dot);
    }
    this.parallax.push({
      container: foreground,
      baseX: 0,
      baseY: 0,
      scrollFactor: 0.35,
    });
    this.layers.push(foreground);

    // Pin every layer to a low depth so it draws behind game entities.
    backdrop.setDepth(0);
    starContainer.setDepth(1);
    foreground.setDepth(1);
  }

  /**
   * Per-frame: shift each parallax layer left by scrollFactor × speed.
   */
  public update(deltaMs: number, scrollSpeed: number): void {
    const dx = (scrollSpeed * deltaMs) / 1000;
    for (const layer of this.parallax) {
      layer.container.x -= dx * layer.scrollFactor;
      if (-layer.container.x > GAME_WIDTH) {
        layer.container.x += GAME_WIDTH;
      } else if (layer.container.x > 0) {
        layer.container.x -= GAME_WIDTH;
      }
    }
  }

  /** Cleanup. Called on scene shutdown. */
  public destroy(): void {
    for (const layer of this.layers) layer.destroy();
    this.layers = [];
    this.parallax = [];
  }
}
